import { Check, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { PageHeadline } from "@/components/ui/Headline";
import { OrganizerShell } from "@/components/layout/OrganizerShell";

interface Plan {
  name: string;
  price: string;
  priceNote?: string;
  description: string;
  popular?: boolean;
  features: string[];
  cta: string;
  ctaVariant?: "primary" | "secondary";
  ctaDisabled?: boolean;
}

const PLANS: Plan[] = [
  {
    name: "Free",
    price: "€0",
    description: "Perfect for first events and small communities.",
    features: [
      "Up to 50 participants",
      "AI moderation",
      "Basic analytics",
      "Email support",
    ],
    cta: "Current plan",
    ctaVariant: "secondary",
    ctaDisabled: true,
  },
  {
    name: "Pro",
    price: "€199",
    priceNote: "/event",
    description: "For meetups, conferences, and recurring networking nights.",
    popular: true,
    features: [
      "Up to 500 participants",
      "AI moderation",
      "Advanced analytics",
      "Priority support",
    ],
    cta: "Upgrade Now",
    ctaVariant: "primary",
  },
  {
    name: "Enterprise",
    price: "Custom",
    description: "White-label and custom integrations for organizers at scale.",
    features: [
      "Unlimited participants",
      "White-label",
      "Custom integrations",
      "Dedicated support",
    ],
    cta: "Contact Us",
    ctaVariant: "secondary",
  },
];

export function PricingPage() {
  return (
    <OrganizerShell>
      <div className="mx-auto max-w-[1080px] animate-fade-in">
        <Link
          to="/events"
          className="inline-flex items-center gap-1 text-[13px] font-semibold text-[#475569] hover:text-[#2563eb]"
        >
          <ArrowLeft className="h-4 w-4" /> My Events
        </Link>

        <div className="mt-4">
          <PageHeadline
            eyebrow="Plans"
            title="Pricing"
            subtitle="Pick the plan that fits your event size — billed per event, no subscriptions."
          />
        </div>

        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          {PLANS.map((plan) => (
            <Card
              key={plan.name}
              padding="md"
              className={
                "relative " + (plan.popular ? "border-2 border-[#2563eb] shadow-[0_4px_24px_rgba(37,99,235,0.12)]" : "")
              }
            >
              {plan.popular ? (
                <Badge tone="primary" className="absolute -top-3 left-1/2 -translate-x-1/2">
                  Most popular
                </Badge>
              ) : null}
              <h3 className="text-[18px] font-bold text-[#1a1a1a]">{plan.name}</h3>
              <p className="mt-1 text-[13px] text-[#6c757d]">{plan.description}</p>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-[32px] font-bold text-[#2563eb]">{plan.price}</span>
                {plan.priceNote ? (
                  <span className="text-[13px] text-[#6c757d]">{plan.priceNote}</span>
                ) : null}
              </div>
              <ul className="mt-5 flex flex-col gap-2 text-[14px] text-[#475569]">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#2563eb]" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                variant={plan.ctaVariant ?? "secondary"}
                disabled={plan.ctaDisabled}
                fullWidth
                className="mt-6"
                size="md"
              >
                {plan.cta}
              </Button>
            </Card>
          ))}
        </div>

        <p className="mt-8 text-center text-[13px] text-[#6c757d]">
          Billing is per event — no subscriptions. Prices shown exclude VAT.
        </p>
      </div>
    </OrganizerShell>
  );
}
